import React, { useState } from 'react';
import { X, BookOpen, Award, CheckCircle2, Download, ChevronRight } from 'lucide-react';

export default function SyllabusModal({ course, onClose, onApplyClick }) {
  const [activeSem, setActiveSem] = useState(1);

  if (!course) return null;

  const currentSyllabus = course.syllabus.find(s => s.sem === activeSem) || course.syllabus[0];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="glass-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '760px',
          maxHeight: '90vh',
          overflowY: 'auto',
          backgroundColor: '#0F2537',
          color: '#FFFFFF',
          padding: '0',
          position: 'relative',
          borderRadius: '20px',
          border: '1px solid rgba(212, 175, 55, 0.3)',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.8)'
        }}
      >
        {/* Modal Header */}
        <div style={{
          backgroundColor: '#07131E',
          color: '#FFFFFF',
          padding: '1.75rem 2rem',
          borderBottom: '2px solid #D4AF37',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          <button
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '1.25rem',
              right: '1.25rem',
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              color: '#FFFFFF',
              borderRadius: '50%',
              width: '36px',
              height: '36px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} />
          </button>

          <span className="badge-gold" style={{ marginBottom: '0.5rem' }}>Curriculum & Syllabus</span>
          <h3 style={{ fontSize: '1.6rem', fontWeight: 800, marginTop: '0.2rem', color: '#FFFFFF' }}>
            {course.name}
          </h3>
          <p style={{ fontSize: '0.9rem', color: '#CBD5E1', marginTop: '0.25rem' }}>
            {course.duration} • Total Fee: ₹{course.totalFee.toLocaleString('en-IN')}
          </p>
        </div>

        {/* Modal Body */}
        <div style={{ padding: '2rem' }}>
          
          {/* Eligibility Callout Box */}
          <div style={{
            backgroundColor: 'rgba(56, 189, 248, 0.12)',
            borderLeft: '4px solid #38BDF8',
            padding: '1rem 1.25rem',
            borderRadius: '8px',
            marginBottom: '1.75rem'
          }}>
            <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#38BDF8', marginBottom: '0.2rem' }}>
              Minimum Academic Eligibility
            </div>
            <div style={{ fontSize: '0.88rem', color: '#E2E8F0', lineHeight: 1.4 }}>
              {course.eligibility}
            </div>
          </div>

          <h4 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#FEF08A', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <BookOpen size={18} color="#D4AF37" /> Semester Breakdown & Subjects
          </h4>

          {/* Semester Selector Buttons */}
          <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '0.75rem', marginBottom: '1.25rem' }}>
            {course.syllabus.map((s) => (
              <button
                key={s.sem}
                onClick={() => setActiveSem(s.sem)}
                style={{
                  padding: '0.55rem 1rem',
                  borderRadius: '8px',
                  fontSize: '0.88rem',
                  fontWeight: activeSem === s.sem ? 700 : 500,
                  backgroundColor: activeSem === s.sem ? '#D4AF37' : '#07131E',
                  color: activeSem === s.sem ? '#0F172A' : '#CBD5E1',
                  border: activeSem === s.sem ? 'none' : '1px solid rgba(255, 255, 255, 0.15)',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease'
                }}
              >
                {s.title}
              </button>
            ))}
          </div>

          {/* Current Semester Subjects Grid */}
          <div style={{
            backgroundColor: '#07131E',
            borderRadius: '12px',
            padding: '1.5rem',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            marginBottom: '1.75rem'
          }}>
            <h5 style={{ fontSize: '1rem', fontWeight: 700, color: '#FFFFFF', marginBottom: '1rem' }}>
              {currentSyllabus.title} Subjects & Modules
            </h5>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.85rem' }}>
              {currentSyllabus.subjects.map((sub, i) => (
                <div key={i} style={{
                  backgroundColor: '#0F2537',
                  padding: '0.85rem 1rem',
                  borderRadius: '8px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.65rem',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  color: '#FFFFFF'
                }}>
                  <CheckCircle2 size={16} color="#34D399" />
                  <span>{sub}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Highlights Checklist */}
          <div style={{ marginBottom: '1.5rem' }}>
            <h5 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#FFFFFF', marginBottom: '0.75rem' }}>
              Program Delivery & Key Features
            </h5>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem' }}>
              {course.keyHighlights.map((h, i) => (
                <div key={i} style={{ fontSize: '0.82rem', color: '#CBD5E1', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <Award size={14} color="#D4AF37" /> {h}
                </div>
              ))}
            </div>
          </div>

          {/* Modal Footer Actions */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '1rem',
            paddingTop: '1.25rem',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => {
                alert(`Detailed PDF syllabus for ${course.name} requested! Download will start...`);
              }}
              className="btn-secondary"
              style={{ padding: '0.75rem 1.25rem', fontSize: '0.88rem' }}
            >
              <Download size={16} color="#D4AF37" /> Download Full PDF Curriculum
            </button>

            <button
              onClick={() => {
                onClose();
                onApplyClick(course);
              }}
              className="btn-primary"
              style={{ padding: '0.75rem 1.5rem', fontSize: '0.88rem' }}
            >
              Apply for {course.code} <ChevronRight size={16} />
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
